import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { getLoggedInAs } from '@realadvisor/auth-client';

import {
  User_Product_Experiences_Update_Column as Column,
  type User_Product_Experiences_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROVIDER,
  type AnalyticsEvent,
  type EventProperties,
  useAnalytics,
} from '@shared/analytics';

import {
  type DismissReason,
  EMPTY_EXPERIENCES,
  type ExperienceRow,
  type ExperienceState,
  type TourOutcome,
  rowKey,
  shouldStoreTourOutcome,
  toExperienceState,
} from './experienceState';
import {
  GET_MY_PRODUCT_EXPERIENCES,
  RECORD_PRODUCT_EXPERIENCES,
  RESET_MY_PRODUCT_EXPERIENCES,
} from './productExperienceQueries';

// The one place that reads and writes what a user went through: release
// entries seen, tours finished, campaigns that reached them. Backed by
// `user_product_experiences`, with local optimistic rows so the badge and
// tours react before the round trip.
//
// Nothing is written and no event is sent while staff are logged in as an
// agent, or for admin previews: the agent's record and the numbers stay real.

export type ReleaseSurface = 'bell' | 'page';
// What the entry's button did: play its tour, open a page, or ask for access
// to a feature in gradual rollout.
export type ReleaseCtaAction = 'tour' | 'link' | 'request_access';
export type TourTriggerKind = 'auto' | 'explicit' | 'preview';

type TourRun = {
  tourId: string;
  // Set when a campaign started the tour.
  campaignId?: string;
  stepCount: number;
  preview: boolean;
};

type ProductExperiences = {
  // False until the stored rows are loaded: nothing counts as unread and no
  // tour starts on its own before that.
  ready: boolean;
  stateLoaded: boolean;
  state: ExperienceState;
  // Lets synchronous code (tour polling) read the latest state.
  getState: () => ExperienceState;
  markReleasesSeen: (ids: readonly string[], surface: ReleaseSurface) => void;
  trackReleaseCta: (id: string, action: ReleaseCtaAction) => void;
  // First time a campaign reaches the user, shown or held out.
  recordCampaignExposure: (event: {
    campaignId: string;
    tourId: string;
    holdout: boolean;
  }) => void;
  recordTourStarted: (event: TourRun & { trigger: TourTriggerKind }) => void;
  trackTourStep: (event: TourRun & { step: number; anchor: string }) => void;
  recordTourEnded: (
    event: TourRun & {
      step: number; // 1-based
      outcome: TourOutcome;
      reason?: DismissReason;
      startedAt: string;
    },
  ) => void;
  reset: () => Promise<void>;
};

const Context = createContext<ProductExperiences | null>(null);

const OUTCOME_COLUMNS = [
  Column.Outcome,
  Column.OutcomeAt,
  Column.DismissCount,
  Column.DismissReason,
];

const KEEP_COMPLETED: User_Product_Experiences_Bool_Exp = {
  _or: [{ outcome: { _is_null: true } }, { outcome: { _neq: 'completed' } }],
};

const keepCompletedSince = (
  startedAt: string,
): User_Product_Experiences_Bool_Exp => ({
  _or: [...(KEEP_COMPLETED._or ?? []), { outcome_at: { _lt: startedAt } }],
});

export const ProductExperiencesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { me, hasuraRole } = useAppData();
  const userId = me?.id;
  const canLoad = userId != null && hasuraRole !== 'user';
  const { track } = useAnalytics();
  const isImpersonating = getLoggedInAs() != null;
  const canRecord = userId != null && !isImpersonating;

  const { data, loading, error, refetch } = useQuery(
    GET_MY_PRODUCT_EXPERIENCES,
    {
      variables: { userId: userId ?? '' },
      skip: !canLoad,
    },
  );
  const [record] = useMutation(RECORD_PRODUCT_EXPERIENCES);
  const [resetRows] = useMutation(RESET_MY_PRODUCT_EXPERIENCES);

  // Optimistic rows, layered over the stored ones until the next load.
  const [localRows, setLocalRows] = useState<Record<string, ExperienceRow>>({});
  const [isCleared, setIsCleared] = useState(false);

  const state = useMemo(() => {
    const stored = isCleared
      ? []
      : data?.users_by_pk?.product_experiences ?? [];
    const merged = new Map<string, ExperienceRow>();
    for (const row of stored) {
      merged.set(rowKey(row.kind, row.key), row);
    }
    for (const [key, row] of Object.entries(localRows)) {
      merged.set(key, row);
    }
    return merged.size === 0
      ? EMPTY_EXPERIENCES
      : toExperienceState(merged.values());
  }, [data, localRows, isCleared]);

  const stateRef = useRef(state);
  stateRef.current = state;
  const getState = useCallback(() => stateRef.current, []);

  // A missing table (an older local database) must not break the CRM: the
  // feed then treats everything as new for this session.
  const ready = userId != null && (!canLoad || !loading || error != null);
  const stateLoaded = canLoad && !loading && error == null;

  const send = useCallback(
    (
      event: AnalyticsEvent,
      properties: EventProperties,
      { preview = false, posthogOnly = false } = {},
    ) => {
      if (isImpersonating || preview) {
        return;
      }
      track(
        event,
        properties,
        posthogOnly ? { exclude: [ANALYTICS_PROVIDER.CUSTOMERIO] } : undefined,
      );
    },
    [isImpersonating, track],
  );

  // Upsert: `updateColumns` empty = keep an existing row as is.
  const write = useCallback(
    (
      row: ExperienceRow,
      updateColumns: Column[],
      where?: User_Product_Experiences_Bool_Exp,
    ) => {
      setLocalRows(current => ({
        ...current,
        [rowKey(row.kind, row.key)]: row,
      }));
      if (!canRecord || !stateLoaded) {
        return;
      }
      const { first_seen_at: _firstSeenAt, ...object } = row;
      record({
        variables: { objects: [object], updateColumns, where },
      }).catch(writeError => {
        console.warn('Could not record product experience', writeError);
        if (row.kind === 'release') {
          return;
        }
        const key = rowKey(row.kind, row.key);
        setLocalRows(current => {
          if (current[key] !== row) {
            return current;
          }
          const { [key]: _failed, ...rest } = current;
          return rest;
        });
      });
    },
    [canRecord, stateLoaded, record],
  );

  const markReleasesSeen = useCallback(
    (ids: readonly string[], surface: ReleaseSurface) => {
      const now = new Date().toISOString();
      for (const id of ids) {
        if (stateRef.current.seenReleases[id] == null) {
          write({ kind: 'release', key: id, first_seen_at: now }, []);
          send(ANALYTICS_EVENTS.RELEASE_VIEWED, { release_id: id, surface });
        }
      }
    },
    [write, send],
  );

  const trackReleaseCta = useCallback(
    (id: string, action: ReleaseCtaAction) =>
      send(ANALYTICS_EVENTS.RELEASE_CTA_CLICKED, {
        release_id: id,
        action,
        has_tour: action === 'tour',
      }),
    [send],
  );

  const recordCampaignExposure: ProductExperiences['recordCampaignExposure'] =
    useCallback(
      ({ campaignId, tourId, holdout }) => {
        if (stateRef.current.campaigns[campaignId] != null) {
          return;
        }
        write(
          {
            kind: 'campaign',
            key: campaignId,
            first_seen_at: new Date().toISOString(),
            holdout,
          },
          [],
        );
        send(ANALYTICS_EVENTS.CAMPAIGN_EXPOSED, {
          campaign_id: campaignId,
          tour_id: tourId,
          holdout,
        });
      },
      [write, send],
    );

  const runProperties = ({ tourId, campaignId, stepCount }: TourRun) => ({
    tour_id: tourId,
    step_count: stepCount,
    ...(campaignId != null && { campaign_id: campaignId }),
  });

  const recordTourStarted: ProductExperiences['recordTourStarted'] =
    useCallback(
      run => {
        if (!run.preview && stateRef.current.tours[run.tourId] == null) {
          write(
            {
              kind: 'tour',
              key: run.tourId,
              first_seen_at: new Date().toISOString(),
              step_count: run.stepCount,
            },
            [],
          );
        }
        send(
          ANALYTICS_EVENTS.TOUR_STARTED,
          { ...runProperties(run), trigger: run.trigger },
          { preview: run.preview },
        );
      },
      [write, send],
    );

  const trackTourStep: ProductExperiences['trackTourStep'] = useCallback(
    run =>
      send(
        ANALYTICS_EVENTS.TOUR_STEP_VIEWED,
        { ...runProperties(run), step: run.step, anchor: run.anchor },
        { preview: run.preview, posthogOnly: true },
      ),
    [send],
  );

  const recordTourEnded: ProductExperiences['recordTourEnded'] = useCallback(
    run => {
      const { tourId, campaignId, outcome, reason, step, stepCount } = run;
      if (!run.preview) {
        const now = new Date().toISOString();
        const dismissed = outcome === 'dismissed';
        const tour = stateRef.current.tours[tourId];
        if (shouldStoreTourOutcome(tour?.outcome, outcome)) {
          write(
            {
              kind: 'tour',
              key: tourId,
              first_seen_at: tour?.at ?? now,
              outcome,
              outcome_at: now,
              last_step: step,
              step_count: stepCount,
              dismiss_reason: dismissed ? reason : null,
              dismiss_count: (tour?.dismissCount ?? 0) + (dismissed ? 1 : 0),
            },
            [...OUTCOME_COLUMNS, Column.LastStep, Column.StepCount],
            dismissed ? KEEP_COMPLETED : undefined,
          );
        }
        if (campaignId != null) {
          const campaign = stateRef.current.campaigns[campaignId];
          write(
            {
              kind: 'campaign',
              key: campaignId,
              first_seen_at: campaign?.firstSeenAt ?? now,
              holdout: false,
              outcome,
              outcome_at: now,
              dismiss_reason: dismissed ? reason : null,
              dismiss_count:
                (campaign?.dismissCount ?? 0) + (dismissed ? 1 : 0),
            },
            OUTCOME_COLUMNS,
            dismissed ? keepCompletedSince(run.startedAt) : undefined,
          );
        }
      }
      send(
        outcome === 'completed'
          ? ANALYTICS_EVENTS.TOUR_COMPLETED
          : ANALYTICS_EVENTS.TOUR_DISMISSED,
        {
          ...runProperties(run),
          step,
          ...(reason != null && { reason }),
        },
        { preview: run.preview },
      );
    },
    [write, send],
  );

  const reset = useCallback(async () => {
    setLocalRows({});
    setIsCleared(true);
    try {
      if (canRecord) {
        await resetRows({ variables: { userId } });
        await refetch();
      }
    } finally {
      setIsCleared(false);
    }
  }, [canRecord, resetRows, refetch, userId]);

  const value = useMemo(
    () => ({
      ready,
      stateLoaded,
      state,
      getState,
      markReleasesSeen,
      trackReleaseCta,
      recordCampaignExposure,
      recordTourStarted,
      trackTourStep,
      recordTourEnded,
      reset,
    }),
    [
      ready,
      stateLoaded,
      state,
      getState,
      markReleasesSeen,
      trackReleaseCta,
      recordCampaignExposure,
      recordTourStarted,
      trackTourStep,
      recordTourEnded,
      reset,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useProductExperiences = (): ProductExperiences => {
  const value = useContext(Context);
  if (value == null) {
    throw new Error(
      'useProductExperiences must be used inside ProductExperiencesProvider',
    );
  }
  return value;
};
